// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { IconMemo } from "@insite/mobius/Icon";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import injectCss from "@insite/mobius/utilities/injectCss";
import * as React from "react";
import styled, { css } from "styled-components";

// XNG-Change
const toggleSizes = {
    default: {
        circleSize: 12,
        height: 16,
        width: 32,
        uncheckedOffset: -14,
    },
    // small: {
    //     circleSize: 12,
    //     height: 16,
    //     width: 36,
    //     uncheckedOffset: -20,
    // },
};

const Slider = styled.span`
    margin: 0px 17px;
    width: 32px;
    height: 16px;
    position: absolute;
    left: 0;
    transition: all ${getProp("theme.transition.duration.regular", "300")}ms ease-in-out;
    display: flex;
    align-items: center;
`;

type SliderFrameProps = {
    _sizeVariant: keyof typeof toggleSizes;
    _color?: string;
};

// XNG-Change
const SliderFrame = styled.div<SliderFrameProps>`
    min-width: ${({ _sizeVariant }) => toggleSizes[_sizeVariant].width}px;
    height: ${({ _sizeVariant }) => toggleSizes[_sizeVariant].height}px;
    border-radius: 10px;
    overflow: hidden;
    position: relative;
    display: inline-block;
    box-sizing: border-box;
    border: none;
    background: ${getColor("common.ui11")};
    color: ${getColor("common.ui7")};
    fill: ${getColor("common.ui7")};

    &[aria-checked="false"] ${Slider} {
        transform: translateX(${({ _sizeVariant }) => toggleSizes[_sizeVariant].uncheckedOffset}px);
    }
    &[aria-checked="true"] {
        color: ${getColor("primary")};
        fill: ${getColor("primary")};
    }
    &&&:hover {
        color: ${getColor("common.ui8")};
        fill: ${getColor("common.ui8")};
    }

    &[aria-checked="true"] ${Slider} {
        transform: translateX(0);
    }

    &[aria-checked="true"]:focus {
        outline: none;
    }
    &:focus {
        outline: none;
    }
    & + label {
        margin-left: 8px;
    }
    &&&[aria-disabled="true"] {
        opacity: 0.33;
        cursor: not-allowed;
        color: ${getColor("common.ui7")};
        fill: ${getColor("common.ui7")};
    }
    ${injectCss}
`;

type Props = SliderFrameProps & React.InputHTMLAttributes<HTMLInputElement>;

const ToggleInput: React.FC<Props> = ({ _sizeVariant = "default", ...otherProps }) => (
    <SliderFrame _sizeVariant={_sizeVariant} {...otherProps}>
        <Slider>
            <IconMemo
                src="FillCircle"
                size={toggleSizes[_sizeVariant].circleSize}
                color="currentColor"
                css={css`
                    margin: 0;
                `}
            />
        </Slider>
    </SliderFrame>
);

export default ToggleInput;
